/**
 * Pure layout for the context graph: contract nodes/edges in, positioned xyflow
 * nodes/edges out.
 *
 * Kept free of React and of xyflow's runtime so it can be unit-tested as the
 * function it is.
 *
 * The graph is a left-to-right causal story, and which column a card belongs in
 * is a property of its KIND, not of which way the contract happened to point an
 * edge. Letting dagre derive ranks from edge direction put source documents
 * mid-canvas, because the contract says `company —GOVERNED_BY→ document`. So
 * the columns are assigned by `KIND_RANK` and the layout graph is oriented to
 * agree with them; see `layoutContextGraph`.
 *
 * Card sizes are computed here, not measured in the DOM: dagre needs a box
 * before anything is rendered, and a layout that shifts once the real text
 * lands reads as a bug. The estimate mirrors `ContextGraphNode`'s box model
 * (12px/14px padding plus the 4px kind accent, 14.5px/21px label, 13px/18px
 * sublabel), so the two must move together.
 *
 * WHAT THIS MODULE DELIBERATELY DOES NOT DO: route edges. An earlier version
 * computed an orthogonal polyline per edge through the empty gutters between
 * columns, and drew that. It had two fatal properties. Every edge between the
 * same pair of columns took the SAME gutter x, so six edges were rendered
 * exactly collinear and none of them could be traced; and the polyline was
 * frozen at layout time, so a card the reader dragged left its own edges
 * behind. Edges are now béziers computed by the edge component from the LIVE
 * handle positions — they diverge naturally, and they follow a dragged card.
 * All this module decides is which side of each card an edge leaves from.
 */

import dagre from "@dagrejs/dagre";
import type { Edge, Node } from "@xyflow/react";

import type {
	ContextEdge,
	ContextGraph,
	ContextNode,
	ContextNodeKind,
} from "../../types/contextGraph";

export const RANK_SEPARATION = 110;
/**
 * Generous on purpose: it makes the graph tall enough that fitting its width no
 * longer leaves a wide empty band above and below.
 *
 * It is a MAXIMUM, not a constant — see `verticalSpacing`.
 */
export const NODE_SEPARATION = 140;

/**
 * The floor the spacing may be squeezed to, and the column height it is
 * squeezed to stay under.
 *
 * A generous gutter is worth having until a column gets crowded, at which point
 * it is what makes the graph too tall to read: eleven metric cards at the full
 * separation are a 2100px column, and fitting that into a canvas costs more
 * legibility than the gutter buys. Past that point the cards keep their size
 * and the air between them gives way.
 */
export const MIN_NODE_SEPARATION = 24;
const TARGET_COLUMN_HEIGHT = 900;

/**
 * How much air to leave between cards, given the tallest column in this graph.
 *
 * One value for the whole graph rather than per column, so rows still read
 * across the canvas.
 */
export function verticalSpacing(columnHeights: readonly number[][]): number {
	let spacing = NODE_SEPARATION;
	for (const heights of columnHeights) {
		if (heights.length < 2) continue;
		const cards = heights.reduce((sum, height) => sum + height, 0);
		const available = (TARGET_COLUMN_HEIGHT - cards) / (heights.length - 1);
		spacing = Math.min(spacing, available);
	}
	return Math.max(MIN_NODE_SEPARATION, Math.min(NODE_SEPARATION, spacing));
}

/**
 * Which column each kind belongs in, left to right: the source material, then
 * the thing it is about, then the numbers, then the test, then the verdict.
 *
 * This is the layout's spine. Kinds that share a rank share a column — with
 * one refinement, made per NODE rather than per kind: a metric that was
 * computed from other metrics sits one column right of its deepest input (see
 * `derivationDepths` / `nodeRanks`), so a COMPUTES edge reads left to right
 * like every other relation instead of looping out of a single metric column.
 *
 * `fund` gets a column TO ITSELF, and pays a column's width for it. Sharing the
 * leftmost column with `document` — pinned to the top, in its own paint — still
 * read as "the fund is the first of these documents", and in a real room, where
 * the fund sits above nine document cards, that is what it looks like. The fund
 * is the room the other cards are in, not one of the things in it, and a column
 * is how this layout says so.
 *
 * This is the MESSAGE spine, and it is also the default. An answer is a story
 * about evidence: the source documents are on the left because that is where
 * the figures come FROM, and the company sits between its paperwork and its
 * numbers.
 */
export const KIND_RANK: Record<ContextNodeKind, number> = {
	fund: 0,
	document: 1,
	company: 2,
	clause: 2,
	// Its own column, right of the companies. Interleaved among them it dragged
	// every `AS OF` edge back across the company cards; here the relation reads
	// left to right like the rest of the graph.
	period: 3,
	metric: 4,
	covenant: 5,
	status: 6,
};

/**
 * The ROOM spine, which is a different story and so a different order.
 *
 * A room is not an argument, it is an inventory: this fund holds these
 * borrowers, and each borrower has filed these kinds of paperwork. So it reads
 * `fund → company → document`, and a document sits to the RIGHT of the company
 * it belongs to rather than to its left. Under the message ranks the same
 * catalog read backwards — documents first, companies after — which is why a
 * room looked like a wall of paperwork with the borrowers hidden behind it.
 *
 * `period` follows the documents; the answer-layer kinds never appear in a room
 * but are ranked past them so the map stays total.
 */
export const ROOM_KIND_RANK: Record<ContextNodeKind, number> = {
	fund: 0,
	company: 1,
	document: 2,
	period: 3,
	clause: 4,
	metric: 5,
	covenant: 6,
	status: 7,
};

/**
 * The column order the given scope reads in.
 *
 * A conversation (`chat` scope) is the union of its answers and reads the way
 * one answer does.
 */
export function ranksFor(scope: ContextGraph["scope"]): Record<ContextNodeKind, number> {
	return scope === "room" ? ROOM_KIND_RANK : KIND_RANK;
}

/**
 * How many COMPUTES hops sit behind each node: the length of the longest
 * derivation chain into it.
 *
 * A plainly cited figure is 0. A figure derived from cited figures is 1. A
 * figure derived from THAT one is 2, and so on — an EBITDA bridge recorded one
 * step at a time is a chain, and the chain is what the reader should see. A
 * clause used as an input (§1.01's cap rate) contributes as a depth-0 source;
 * its own column is the clause column regardless.
 *
 * Deterministic and total: a cycle in the COMPUTES edges cannot come from the
 * evidence registry (a derivation names only ids minted before it), but a
 * malformed payload must not hang the layout, so a node reached again while
 * still being resolved counts as 0 rather than recursing forever.
 */
export function derivationDepths(
	graph: Pick<ContextGraph, "nodes" | "edges">,
): Map<string, number> {
	const inputs = new Map<string, string[]>();
	for (const edge of graph.edges) {
		if (edge.relation !== "COMPUTES") continue;
		const list = inputs.get(edge.dst);
		if (list) list.push(edge.src);
		else inputs.set(edge.dst, [edge.src]);
	}

	const depths = new Map<string, number>();
	const resolving = new Set<string>();

	const depthOf = (id: string): number => {
		const known = depths.get(id);
		if (known !== undefined) return known;
		if (resolving.has(id)) return 0;
		resolving.add(id);
		let depth = 0;
		for (const src of inputs.get(id) ?? []) {
			depth = Math.max(depth, depthOf(src) + 1);
		}
		resolving.delete(id);
		depths.set(id, depth);
		return depth;
	};

	for (const node of graph.nodes) depthOf(node.id);
	return depths;
}

/**
 * The column of every node: its kind's rank, pushed right by derivation depth.
 *
 * Only metrics spread by depth. The covenant and status columns move right by
 * the DEEPEST metric's depth, so the test and the verdict stay to the right of
 * every figure however long the chain that produced it — the story still ends
 * where it did.
 */
export function nodeRanks(
	graph: Pick<ContextGraph, "nodes" | "edges">,
	ranks: Record<ContextNodeKind, number>,
): Map<string, number> {
	const depths = derivationDepths(graph);
	let deepest = 0;
	for (const node of graph.nodes) {
		if (node.kind === "metric") deepest = Math.max(deepest, depths.get(node.id) ?? 0);
	}

	const out = new Map<string, number>();
	for (const node of graph.nodes) {
		const base = ranks[node.kind];
		if (node.kind === "metric") out.set(node.id, base + (depths.get(node.id) ?? 0));
		else if (node.kind === "covenant" || node.kind === "status") out.set(node.id, base + deepest);
		else out.set(node.id, base);
	}
	return out;
}

/** Per-kind width bounds. The estimate below is clamped into these. */
const WIDTH_BOUNDS: Record<ContextNodeKind, { min: number; max: number }> = {
	fund: { min: 210, max: 260 },
	company: { min: 240, max: 290 },
	period: { min: 210, max: 260 },
	document: { min: 210, max: 280 },
	clause: { min: 210, max: 300 },
	metric: { min: 210, max: 270 },
	covenant: { min: 210, max: 280 },
	status: { min: 210, max: 260 },
};

/**
 * Horizontal chrome the text does not get: 14px padding either side, plus the
 * 4px kind accent and the 2px it is inset by.
 */
const HORIZONTAL_PADDING = 34;
const VERTICAL_PADDING = 24;
const BORDER = 2;
const LABEL_LINE_HEIGHT = 21;
const SUBLABEL_LINE_HEIGHT = 18;
/**
 * Advance width per character for Inter at 14.5px/600 and 13px/400.
 *
 * Deliberately on the high side. The estimate decides the box dagre packs, and
 * the two errors are not symmetric: over-estimating leaves a little air inside a
 * card, while under-estimating lets the real text wrap to a line the box has no
 * room for, and the card then sits on top of the one below it. Real labels
 * ("Other management adjustments") are wider per character than the fixture's.
 */
const LABEL_CHAR_WIDTH = 8.8;
const SUBLABEL_CHAR_WIDTH = 7.3;
/** Slack against the same rounding, when deciding how many lines text needs. */
const WRAP_MARGIN = 6;
/** Width the `FINAL`-style chip takes on the label row. */
const BADGE_WIDTH = 50;

/**
 * Whether a node's sublabel is worth a second line.
 *
 * A document whose badge and sublabel say the same thing (`FINAL`) should show
 * the chip once, not the chip and an echo of it underneath.
 */
export function showsSublabel(node: Pick<ContextNode, "sublabel" | "badge">): boolean {
	if (!node.sublabel) return false;
	return node.sublabel !== node.badge;
}

export interface NodeSize {
	width: number;
	height: number;
}

export function measureNode(node: ContextNode): NodeSize {
	const bounds = WIDTH_BOUNDS[node.kind];
	const withSublabel = showsSublabel(node);

	const badgeWidth = node.badge ? BADGE_WIDTH : 0;
	const labelWidth = node.label.length * LABEL_CHAR_WIDTH + badgeWidth;
	const sublabelWidth = withSublabel
		? (node.sublabel as string).length * SUBLABEL_CHAR_WIDTH
		: 0;

	const width = Math.round(
		Math.min(
			bounds.max,
			Math.max(bounds.min, Math.max(labelWidth, sublabelWidth) + HORIZONTAL_PADDING),
		),
	);

	// A label longer than the kind's max width wraps rather than being clipped,
	// so the box has to grow with it. Before this, a wrapped card overflowed the
	// height dagre packed for it and sat on top of the card below.
	const content = width - HORIZONTAL_PADDING - WRAP_MARGIN;
	const labelLines = lineCount(labelWidth, content);
	const sublabelLines = withSublabel ? lineCount(sublabelWidth, content) : 0;

	const height =
		VERTICAL_PADDING +
		BORDER +
		labelLines * LABEL_LINE_HEIGHT +
		sublabelLines * SUBLABEL_LINE_HEIGHT;

	return { width, height };
}

/** How many lines `textWidth` needs at `available` px per line. */
function lineCount(textWidth: number, available: number): number {
	if (available <= 0) return 1;
	return Math.max(1, Math.ceil(textWidth / available));
}

/** What `ContextGraphNode` reads off the laid-out node. */
export interface ContextNodeData extends Record<string, unknown> {
	node: ContextNode;
	/** An endpoint of at least one `on_path` edge. */
	onPath: boolean;
	/**
	 * A focus is set and this node is neither in its neighbourhood nor on the
	 * reasoning path — context, not this question.
	 */
	dimmed: boolean;
	size: NodeSize;
}

/** What `ContextGraphEdge` reads off the laid-out edge. */
export interface ContextEdgeData extends Record<string, unknown> {
	edge: ContextEdge;
	dimmed: boolean;
	/**
	 * Whether this edge shows its relation word without being hovered.
	 *
	 * There is no geometry here on purpose. The edge draws itself from the live
	 * handle positions, so it also knows where its own midpoint is — and that
	 * midpoint stays correct when the reader drags a card, which a position
	 * frozen at layout time did not.
	 */
	showLabel: boolean;
}

export type ContextFlowNode = Node<ContextNodeData, "contextNode">;
export type ContextFlowEdge = Edge<ContextEdgeData, "contextEdge">;

export interface LaidOutGraph {
	nodes: ContextFlowNode[];
	edges: ContextFlowEdge[];
}

/**
 * The focus node plus everything one hop away, in either direction.
 *
 * Returns `null` when no focus is set — the caller uses that to mean "nothing
 * is dimmed", which is not the same as "the neighbourhood is empty".
 */
export function focusNeighbourhood(
	graph: Pick<ContextGraph, "nodes" | "edges">,
	focus: string | null,
): Set<string> | null {
	if (focus === null) return null;
	if (!graph.nodes.some((node) => node.id === focus)) return null;

	const neighbourhood = new Set<string>([focus]);
	for (const edge of graph.edges) {
		if (edge.src === focus) neighbourhood.add(edge.dst);
		if (edge.dst === focus) neighbourhood.add(edge.src);
	}
	return neighbourhood;
}

/**
 * How an edge should be fed to dagre so the columns come out in `KIND_RANK`
 * order.
 *
 * The contract points several edges against the reading order —
 * `company —GOVERNED_BY→ document` is the clearest, and it is what used to
 * drag source documents into the middle of the canvas. Feeding dagre an edge
 * that runs from the lower-ranked kind to the higher one keeps its vertical
 * ordering consistent with the columns we are about to impose.
 *
 * This affects the ORDERING PASS only. The rendered edge keeps the contract's
 * own direction, which is what the arrowheads and relation labels describe.
 */
export function layoutOrientation(
	srcKind: ContextNodeKind,
	dstKind: ContextNodeKind,
	ranks: Record<ContextNodeKind, number> = KIND_RANK,
): boolean {
	return ranks[dstKind] < ranks[srcKind];
}

/**
 * How many times one relation may be written on the canvas.
 *
 * The old renderer labelled every edge. One live answer draws nine `SOURCE`
 * edges and seven `HAS METRIC` edges, so the reader got nine copies of the same
 * word, each sitting on a line — and because several of those lines were drawn
 * on top of one another, the words stacked into a column that named nothing.
 * Two is enough to establish a relation without turning it into wallpaper.
 */
export const MAX_LABELS_PER_RELATION = 2;

/**
 * Which edges show their relation word without being hovered.
 *
 * Two rules:
 *
 *   1. a relation gets at most `MAX_LABELS_PER_RELATION` words on the canvas,
 *      and the ANSWER'S OWN edges claim them first — if `SETS BASIS` is written
 *      twice, it is written on the two that carry the reasoning, not on two
 *      context edges that happened to come first in the payload;
 *   2. never twice off the same card, so a fan of eight `HAS METRIC` edges
 *      leaving one company is labelled once rather than eight times.
 *
 * Everything else stays silent until hovered — `ContextGraphEdge` reveals the
 * word on pointer-over and keeps it in the path's `<title>` throughout, so it
 * is never actually lost.
 *
 * Deterministic: the on-path partition is stable and each half keeps contract
 * order, so the same graph always labels the same edges.
 */
export function labelledEdges(edges: readonly ContextEdge[]): Set<string> {
	const written = new Map<string, number>();
	const claimedSource = new Set<string>();
	const claimedTarget = new Set<string>();
	const labelled = new Set<string>();

	// The answer's chain first; the catalog around it takes what is left.
	const byPriority = [
		...edges.filter((edge) => edge.on_path),
		...edges.filter((edge) => !edge.on_path),
	];

	for (const edge of byPriority) {
		if ((written.get(edge.relation) ?? 0) >= MAX_LABELS_PER_RELATION) continue;

		const fromKey = `${edge.relation} ${edge.src}`;
		const toKey = `${edge.relation} ${edge.dst}`;
		if (claimedSource.has(fromKey) || claimedTarget.has(toKey)) continue;

		claimedSource.add(fromKey);
		claimedTarget.add(toKey);
		written.set(edge.relation, (written.get(edge.relation) ?? 0) + 1);
		labelled.add(edge.id);
	}

	return labelled;
}

/**
 * Which side of each card an edge should leave from and arrive at.
 *
 * Every card exposes an anchor on all four sides; picking the pair that faces
 * the way the edge actually travels is what keeps the bézier from doubling back
 * on itself. A Right→Left pair on an edge that runs leftwards traces a loop,
 * which on this canvas reads as a phantom empty card.
 */
export function handleSides(
	from: { column: number; y: number },
	to: { column: number; y: number },
): { sourceHandle: string; targetHandle: string } {
	if (to.column > from.column) return { sourceHandle: "s-right", targetHandle: "t-left" };
	if (to.column < from.column) return { sourceHandle: "s-left", targetHandle: "t-right" };
	// Same column: BOTH ends leave to the right, so the curve bulges out into
	// the empty gutter and comes back. Top-to-bottom instead sends it straight
	// down the column, through every card stacked between the two — which is
	// exactly what `doc —SUPERSEDES→ drafts` did to the credit agreement
	// sitting between them.
	return { sourceHandle: "s-right", targetHandle: "t-right" };
}

/** Ids of nodes touched by an `on_path` edge. */
function onPathNodes(edges: readonly ContextEdge[]): Set<string> {
	const ids = new Set<string>();
	for (const edge of edges) {
		if (!edge.on_path) continue;
		ids.add(edge.src);
		ids.add(edge.dst);
	}
	return ids;
}

/**
 * Ask dagre for a vertical ordering.
 *
 * Only the ordering is kept. Dagre is very good at deciding which of four
 * metrics should sit above the others so the edges cross least, and bad at
 * putting a card in the column the reader expects — so it does the first job
 * and `assignColumns` does the second. (Feeding it `minlen: 0` to pin a column
 * directly is not an option: dagre throws on it.)
 */
function verticalOrder(
	graph: ContextGraph,
	sizes: Map<string, NodeSize>,
	rankOf: Map<string, number>,
): Map<string, number> {
	const dag = new dagre.graphlib.Graph();
	dag.setDefaultEdgeLabel(() => ({}));
	dag.setGraph({ rankdir: "LR", ranksep: RANK_SEPARATION, nodesep: NODE_SEPARATION });

	for (const node of graph.nodes) {
		const size = sizes.get(node.id) as NodeSize;
		dag.setNode(node.id, { width: size.width, height: size.height });
	}

	for (const edge of graph.edges) {
		const srcRank = rankOf.get(edge.src);
		const dstRank = rankOf.get(edge.dst);
		if (srcRank === undefined || dstRank === undefined) continue;
		// The same rule as `layoutOrientation`, over node ranks rather than
		// kind ranks: an edge pointing against the columns is fed in reversed.
		const [from, to] = dstRank < srcRank ? [edge.dst, edge.src] : [edge.src, edge.dst];
		dag.setEdge(from, to);
	}

	dagre.layout(dag);

	const order = new Map<string, number>();
	for (const node of graph.nodes) order.set(node.id, dag.node(node.id).y);
	return order;
}

/**
 * Place every card in its kind's column and stack the column vertically.
 *
 * Columns are as wide as their widest card and each card is centred in its
 * own; within a column the cards keep dagre's vertical ordering and are packed
 * `NODE_SEPARATION` apart, then the column is centred on the shared axis. Doing
 * the packing here rather than trusting dagre's `y` is what guarantees two
 * cards forced into the same column cannot overlap.
 */
export interface ColumnGeometry {
	positions: Map<string, { x: number; y: number }>;
	/** Rank to `[left, right]` of the column, in flow coordinates. */
	bounds: Map<number, [number, number]>;
	/** Rank to the y-intervals its cards occupy, sorted top to bottom. */
	occupied: Map<number, [number, number][]>;
}

function assignColumns(
	graph: ContextGraph,
	sizes: Map<string, NodeSize>,
	order: Map<string, number>,
	rankOf: Map<string, number>,
): ColumnGeometry {
	const columns = new Map<number, ContextNode[]>();
	for (const node of graph.nodes) {
		const rank = rankOf.get(node.id) ?? 0;
		const column = columns.get(rank);
		if (column) column.push(node);
		else columns.set(rank, [node]);
	}

	const ranks = [...columns.keys()].sort((a, b) => a - b);
	const spacing = verticalSpacing(
		ranks.map((rank) =>
			(columns.get(rank) as ContextNode[]).map(
				(node) => (sizes.get(node.id) as NodeSize).height,
			),
		),
	);
	const positions = new Map<string, { x: number; y: number }>();
	const bounds = new Map<number, [number, number]>();
	const occupied = new Map<number, [number, number][]>();
	let x = 0;

	for (const rank of ranks) {
		const column = columns.get(rank) as ContextNode[];
		const width = Math.max(
			...column.map((node) => (sizes.get(node.id) as NodeSize).width),
		);

		// Ties broken by id so the same graph always lays out identically.
		column.sort((a, b) => {
			const delta = (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0);
			return delta !== 0 ? delta : a.id.localeCompare(b.id);
		});

		const heights = column.map((node) => (sizes.get(node.id) as NodeSize).height);
		const total =
			heights.reduce((sum, height) => sum + height, 0) + spacing * (column.length - 1);

		const columnX = x;
		const spans: [number, number][] = [];
		let y = -total / 2;
		for (let index = 0; index < column.length; index += 1) {
			const node = column[index];
			const size = sizes.get(node.id) as NodeSize;
			positions.set(node.id, { x: columnX + (width - size.width) / 2, y });
			spans.push([y, y + heights[index]]);
			y += heights[index] + spacing;
		}
		bounds.set(rank, [columnX, columnX + width]);
		occupied.set(rank, spans);

		x += width + RANK_SEPARATION;
	}

	return { positions, bounds, occupied };
}

/**
 * Lay the graph out left-to-right.
 *
 * `focus` defaults to the graph's own `focus`; passing one explicitly lets the
 * page re-centre without refetching.
 */
export function layoutContextGraph(
	graph: ContextGraph,
	focus: string | null = graph.focus,
): LaidOutGraph {
	const sizes = new Map<string, NodeSize>();
	for (const node of graph.nodes) sizes.set(node.id, measureNode(node));

	const rankOf = nodeRanks(graph, ranksFor(graph.scope));
	const order = verticalOrder(graph, sizes, rankOf);
	const { positions } = assignColumns(graph, sizes, order, rankOf);

	const neighbourhood = focusNeighbourhood(graph, focus);
	const pathNodes = onPathNodes(graph.edges);

	const nodes: ContextFlowNode[] = graph.nodes.map((node) => {
		const size = sizes.get(node.id) as NodeSize;
		const position = positions.get(node.id) as { x: number; y: number };
		// The reasoning path is never dimmed, however far from the focus it
		// runs: it IS the answer, and a focus is a place to start reading, not a
		// filter over the chain that produced the number.
		const emphasised =
			(neighbourhood?.has(node.id) ?? false) || pathNodes.has(node.id);

		return {
			id: node.id,
			type: "contextNode",
			position,
			data: {
				node,
				onPath: pathNodes.has(node.id),
				dimmed: neighbourhood !== null && !emphasised,
				size,
			},
			// The size the columns were packed with, handed to xyflow as the
			// node's initial dimensions so the first paint is not a reflow.
			width: size.width,
			height: size.height,
		};
	});

	// Which side each edge leaves and enters on. The card's position at layout
	// time is enough to decide that; where the line actually lands is read off
	// the live handle by the edge component, so a dragged card keeps its edges.
	const anchors = new Map<string, { column: number; y: number }>(
		graph.nodes.map((node) => {
			const size = sizes.get(node.id) as NodeSize;
			const position = positions.get(node.id) as { x: number; y: number };
			return [
				node.id,
				{ column: rankOf.get(node.id) ?? 0, y: position.y + size.height / 2 },
			];
		}),
	);

	const labelled = labelledEdges(graph.edges);

	const build = (edge: ContextEdge): ContextFlowEdge => {
		const from = anchors.get(edge.src);
		const to = anchors.get(edge.dst);
		const sides = from && to ? handleSides(from, to) : {};

		return {
			id: edge.id,
			type: "contextEdge",
			source: edge.src,
			target: edge.dst,
			...sides,
			data: {
				edge,
				showLabel: labelled.has(edge.id),
				// Context edges dim only when BOTH ends sit outside the
				// neighbourhood — an edge into the focus is part of the story. Path
				// edges never dim, for the same reason path nodes do not.
				dimmed:
					!edge.on_path &&
					neighbourhood !== null &&
					!neighbourhood.has(edge.src) &&
					!neighbourhood.has(edge.dst),
			},
		};
	};

	// The reasoning path paints over the grey context rather than under it,
	// whatever order the contract listed the edges in. Done by ORDER, not by
	// `zIndex`: xyflow renders an edge whose zIndex is above a node's on a
	// layer above the nodes, so lifting the path drew it across the cards it
	// runs between. Within one layer, later edges paint over earlier ones.
	const edges: ContextFlowEdge[] = [
		...graph.edges.filter((edge) => !edge.on_path).map(build),
		...graph.edges.filter((edge) => edge.on_path).map(build),
	];

	return { nodes, edges };
}
